// publicationsStore — client cache of /api/publications response.
//
// One fetch every 15 min keeps the UI fresh without hammering the
// endpoint (which itself edge-caches for 60s). On-demand refresh
// via `refresh()` for when the user opens the Data Currency panel.

import { create } from 'zustand';
import { kvGet, kvSet } from '../lib/idb';

// IDB key for offline persistence.
const IDB_KEY = 'fyrefly:publications:v1';

export type PubStatus = 'current' | 'expiring' | 'expired' | 'unknown';

export interface PublicationEntry {
  id: string;
  label: string;
  status: PubStatus;
  effective: string | null;
  expires: string | null;
  daysUntilExpiry: number | null;
  importedAt: string | null;
  counts?: Record<string, number>;
  note?: string;
  /** Product-specific cycle code. Different products use different
   *  cycle namespaces — d-TPP is 4-digit YYCC (e.g. "2605"); CIFP
   *  is 6-digit AIRAC YYMMDD. Read this rather than deriving from
   *  the effective date. */
  cycle?: string;
}

export interface PublicationsSnapshot {
  computedAt: string;
  publications: PublicationEntry[];
  overall: PubStatus;
  nextExpiry: { id: string; expires: string } | null;
}

interface PublicationsState {
  snapshot: PublicationsSnapshot | null;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  /** Fetch from /api/publications. Returns the snapshot or null on failure. */
  refresh: () => Promise<PublicationsSnapshot | null>;
  /** Ensure data is fresh (fetched within `maxAgeMs`). No-op if it is. */
  ensureFresh: (maxAgeMs?: number) => Promise<PublicationsSnapshot | null>;
}

const FIFTEEN_MIN_MS = 15 * 60 * 1000;

export const usePublicationsStore = create<PublicationsState>((set, get) => {
  // Rehydrate from IDB at store creation so PublicationsBadge has data
  // immediately on cold start / tab eviction (OAC6).
  kvGet<PublicationsSnapshot>(IDB_KEY).then((stored) => {
    if (stored && !get().snapshot) {
      set({ snapshot: stored });
    }
  }).catch(() => { /* IDB read failure is non-fatal */ });

  return {
    snapshot: null,
    loading: false,
    error: null,
    lastFetchedAt: null,

    refresh: async () => {
      set({ loading: true, error: null });
      try {
        // Let the SW own cache freshness (not cache: 'no-store') so the SW
        // serves a cached response offline instead of a 504.
        const r = await fetch('/api/publications');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const snap = (await r.json()) as PublicationsSnapshot;
        // Persist to IDB for cold-start / tab-eviction recovery (OAC6).
        kvSet(IDB_KEY, snap).catch(() => { /* non-fatal */ });
        set({ snapshot: snap, loading: false, lastFetchedAt: Date.now() });
        return snap;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        set({ loading: false, error: msg });
        return null;
      }
    },

    ensureFresh: async (maxAgeMs = FIFTEEN_MIN_MS) => {
      const { snapshot, lastFetchedAt, refresh } = get();
      if (snapshot && lastFetchedAt && Date.now() - lastFetchedAt < maxAgeMs) {
        return snapshot;
      }
      return refresh();
    },
  };
});

// ── Helpers ──────────────────────────────────────────────────────────────

/** Tailwind text color class for the badge / panel summary. */
export function statusTextClass(status: PubStatus): string {
  switch (status) {
    case 'current':  return 'text-emerald-400';
    case 'expiring': return 'text-amber-400';
    case 'expired':  return 'text-red-400';
    case 'unknown':  return 'text-cockpit-300';
  }
}

/** Short human label for the badge. */
export function statusLabel(status: PubStatus): string {
  switch (status) {
    case 'current':  return 'Current';
    case 'expiring': return 'Expiring soon';
    case 'expired':  return 'Expired';
    case 'unknown':  return 'Unknown';
  }
}

/** Format daysUntilExpiry as 'in N days' / 'N days ago' / '—'. */
export function formatDaysUntil(days: number | null): string {
  if (days == null) return '—';
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
  const ago = Math.abs(days);
  return `${ago} day${ago === 1 ? '' : 's'} ago`;
}
